import { useEffect, useMemo, useRef, useState } from 'react'

import { Carousel } from '@hhgtech/hhg-components/mantine'
import { Box, Stack, rem } from '@mantine/core'
import Autoplay from 'embla-carousel-autoplay'
import { useIntl } from 'react-intl'

import { Container } from 'components/atoms/container'
import { Disclaimer } from 'components/atoms/disclaimer'
import { SharePopupNew } from 'components/organisms/sharePopupNew'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useHandlerUrls } from 'hooks/useHandlerUrls'
import { useIsMobile } from 'hooks/useIsMobile'

import useStyles from './index.styles'
import { HeroBannerProps } from '..'
import { MedicalFacts } from '../medicalFacts'
import { Navigator } from '../navigator'
import { BlockItem } from '../navigator/blockItem'
import { SingleBannerItem } from '../singleBannerItem'

const CustomBanner = ({
  mappingIconList,
  colorArr,
  sessionProps: { imageBanner, navigating: navigatingInit = [], medicalFacts },
  navigatorProps,
  styles,
  bannerTracking,
  ratio,
  keepRatio,
  onBannerLoad,
  hasSociaShare,
  autoSlide,
  className,
  classNames,
}: HeroBannerProps) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const navigating = [...navigatingInit].slice(0, 6) // for test layout
  const blockName = useMemo(
    () => navigatorProps?.itemComponent?.displayName || 'FlatIcon',
    [navigating],
  )
  const autoplay = useRef(
    Autoplay({
      delay: 7000,
      stopOnInteraction: false,
    }),
  )
  const { classes, cx } = useStyles(
    {
      numberItem: navigating.length,
      blockName,
    },
    { name: 'CarePathHeroBannerCustomLayout', styles, classNames },
  )

  const listUrl = useHandlerUrls([
    imageBanner?.[0].linkTo,
    ...navigating.map((i) => i.url),
  ])
  const currentUrl = useCurrentUrl()
  const [showSocialShare, setShowSocialShare] = useState(false)

  useEffect(() => {
    setTimeout(() => setShowSocialShare(true), 1000)
  }, [])

  return (
    <>
      <Box className={cx(className, classes.root)}>
        <Box className={classes.wrapper}>
          {imageBanner.length > 1 ? (
            <Carousel
              withIndicators
              withControls={false}
              controlSize={32}
              controlsOffset={5}
              loop
              plugins={autoSlide ? [autoplay.current] : []}
              styles={(theme) => ({
                indicator: {
                  width: '8px',
                  height: '8px',
                  borderWidth: 0,
                  opacity: 1,
                  backgroundColor: theme.colors.neutral[2],
                  '&[data-active]': {
                    backgroundColor: theme.colors.blue[6],
                  },
                  '.mantine-Carousel-control[data-inactive]': {
                    opacity: 0,
                    cursor: 'default',
                  },
                },
                controls: {
                  left: rem(30),
                  right: rem(30),
                  transform: 'translate(0, -50%)',
                },
                control: {
                  width: rem(40),
                  height: rem(40),
                  border: 0,
                },
                arrow: {
                  background: '#fff',
                  opacity: 1,
                },
              })}
            >
              {imageBanner.map((i, idx) => (
                <Carousel.Slide key={String(idx)}>
                  <SingleBannerItem
                    {...i}
                    dataTracking={bannerTracking}
                    keepRatio={keepRatio}
                    ratio={ratio}
                    onBannerLoad={onBannerLoad}
                  />
                </Carousel.Slide>
              ))}
            </Carousel>
          ) : (
            <SingleBannerItem
              {...imageBanner[0]}
              dataTracking={bannerTracking}
              keepRatio={keepRatio}
              onBannerLoad={onBannerLoad}
              ratio={ratio}
            />
          )}
        </Box>
        {(navigating.length > 0 || medicalFacts?.listContent.length > 0) && (
          <Box
            className={classes.navigatorWrapper}
            sx={(theme) => ({
              padding: `${rem(24)} 0`,
              [theme.fn.largerThan('sm')]: {
                padding: `${rem(48)} 0`,
                // ...(blockName === 'ElegantUI' && {
                //   paddingBottom: 0,
                // }),
              },
            })}
          >
            <Container className={classes.navigatorInner}>
              <Stack
                sx={(theme) => ({
                  gap: rem(24),
                  [theme.fn.largerThan('sm')]: {
                    gap: rem(40),
                  },
                })}
              >
                {navigating.length > 0 && (
                  <Navigator
                    mappingIconList={mappingIconList}
                    classNames={{
                      root: classes.navigatorRoot,
                      // item: classes.navigatorItem,
                      // itemImgWrapper: classes.navigatorItemImgWrapper,
                      // itemTitle: classes.navigatorItemTitle,
                    }}
                    colorArr={colorArr}
                    navigating={navigating}
                    {...navigatorProps}
                  >
                    {navigating.map((i, idx) => (
                      <BlockItem
                        data-type={i.type}
                        key={String(idx)}
                        classNames={{
                          root: classes.navigatorItem,
                        }}
                        displayAs={navigatorProps?.itemComponent}
                        isExternalUrl={listUrl[idx + 1]?.isExternalSite}
                        target={
                          listUrl[idx + 1]?.isExternalUrl ? '_blank' : '_self'
                        }
                        {...(blockName === 'FlatIcon' &&
                          isMobile && {
                            layout: 'horizon',
                          })}
                      />
                    ))}
                  </Navigator>
                )}
                {isMobile &&
                  listUrl.filter((i) => i.isExternalSite).length > 0 && (
                    <Disclaimer
                      classNames={{
                        root: classes.disclaimer,
                        title: classes.disclaimerTitle,
                      }}
                      content={f({ id: 'ctaBanner.disclaimer.externalUrl' })}
                      styles={(theme) => ({
                        root: {
                          borderColor: `${theme.colors.neutral[1]} !important`,
                          backgroundColor: theme.colors.neutral[0],
                        },
                      })}
                    />
                  )}
                {medicalFacts.listContent.length > 0 && (
                  <MedicalFacts
                    medicalFacts={medicalFacts}
                    sx={(theme) => ({
                      ...(blockName === 'ElegantUI' && {
                        background: theme.colors.neutral[0],
                      }),
                      [theme.fn.smallerThan('sm')]: {
                        marginLeft: rem(-10),
                        marginRight: rem(-10),
                      },
                    })}
                  />
                )}
              </Stack>
            </Container>
          </Box>
        )}
      </Box>
      {!isMobile && hasSociaShare && (
        <Box
          className={cx(showSocialShare ? 'is-show' : '', classes.shareArea)}
        >
          <SharePopupNew.AsideNav
            shareUrl={currentUrl}
            classNames={{
              root: classes.shareBox,
              copyBtn: classes.shareCopyBtn,
              heading: classes.shareHeading,
            }}
            bgColor="transparent"
          />
        </Box>
      )}
    </>
  )
}

CustomBanner.displayName = 'CustomBanner'

export { CustomBanner }
